import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, Mail, Phone, Send, Image as ImageIcon, CheckCircle, AlertCircle, Plus, X, Search, Paperclip, ChevronLeft } from "lucide-react";
import { useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from 'react-hot-toast';
import { getAssetUrl } from "../../utils/assetUtils";

const SupportPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const selectedTicketId = searchParams.get("id");
    const isNewTicketAction = searchParams.get("action") === "new";

    const [userData, setUserData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [screenshot, setScreenshot] = useState(null);
    const [screenshotPreview, setScreenshotPreview] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);
    const [issue, setIssue] = useState("");
    const [ticketReplies, setTicketReplies] = useState({});
    const [searchTerm, setSearchTerm] = useState("");

    const [chatAttachment, setChatAttachment] = useState(null);
    const [chatAttachmentPreview, setChatAttachmentPreview] = useState(null);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    
    const [token, setToken] = useState(null);

    const chatEndRef = useRef(null);
    const chatFileInputRef = useRef(null);

    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (selectedTicketId) scrollToBottom();
    }, [selectedTicketId, tickets]);

    useEffect(() => {
        const path = location.pathname;
        let activeToken = null;
        
        if (path.includes('/activity')) {
            activeToken = localStorage.getItem('token_partner_activity');
        } else if (path.includes('/restaurant')) {
            activeToken = localStorage.getItem('token_partner_restaurant');
        } else if (path.includes('/shop')) {
            activeToken = localStorage.getItem('token_partner_shop');
        }
        
        setToken(activeToken);

        const fetchUserData = async () => {
            if (!activeToken) return;
            try {
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/auth/profile/`, {
                    headers: { Authorization: `Bearer ${activeToken}` }
                });
                setUserData(response.data);
            } catch (error) {
                console.error("Error fetching user profile:", error);
            }
        };

        const fetchTickets = async () => {
            if (!activeToken) return;
            try {
                setLoadingTickets(true);
                const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/agent/tickets/history`, {
                    headers: { Authorization: `Bearer ${activeToken}` }
                });
                setTickets(response.data);
            } catch (error) {
                console.error("Error fetching tickets:", error);
            } finally {
                setLoadingTickets(false);
            }
        };

        fetchUserData();
        fetchTickets();
    }, [location.pathname]);

    const fetchTicketsData = async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/agent/tickets/history`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(response.data);
        } catch (error) {
            console.error("Error fetching tickets:", error);
        }
    };

    const handleScreenshotChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setScreenshot(file);
            const reader = new FileReader();
            reader.onloadend = () => setScreenshotPreview(reader.result);
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    const handleChatAttachmentChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setChatAttachment(file);
            const reader = new FileReader();
            reader.onloadend = () => setChatAttachmentPreview(reader.result);
            reader.readAsDataURL(file);
        }
        e.target.value = '';
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting || !issue.trim() || !token) {
            if (!issue.trim()) toast.error("Please describe your issue.");
            return;
        }

        try {
            setIsSubmitting(true);
            const formData = new FormData();
            formData.append("issue_description", issue);
            formData.append("agent_name", userData?.name || "");
            formData.append("agent_email", userData?.email || "");
            formData.append("agent_phone", userData?.phone_number || "");
            if (screenshot) formData.append("screenshot", screenshot);

            const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/agent/tickets`, formData, {
                headers: { 
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`
                }
            });

            toast.success("Ticket raised successfully!");
            setIssue("");
            setScreenshot(null);
            setScreenshotPreview(null);
            await fetchTicketsData();
            if (response.data?.ticket?._id) setSearchParams({ id: response.data.ticket._id });
        } catch (error) {
            toast.error("Failed to raise ticket.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSendMessage = async (ticketId) => {
        const message = ticketReplies[ticketId];
        if ((!message || !message.trim()) && !chatAttachment) return;
        if (!token) return;

        try {
            setIsSendingMessage(true);
            const formData = new FormData();
            formData.append("message", message || "");
            if (chatAttachment) formData.append("attachment", chatAttachment);

            await axios.post(`${process.env.REACT_APP_BASE_URL}/agent/tickets/${ticketId}/message`, formData, {
                headers: { 
                    "Content-Type": "multipart/form-data",
                    Authorization: `Bearer ${token}`
                }
            });

            setTicketReplies(prev => ({ ...prev, [ticketId]: "" }));
            setChatAttachment(null);
            setChatAttachmentPreview(null);

            await fetchTicketsData();
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            toast.error("Failed to send message");
        } finally {
            setIsSendingMessage(false);
        }
    };

    const filteredTickets = tickets.filter(t =>
        t.issue_description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t._id.includes(searchTerm)
    );

    const selectedTicket = tickets.find(t => t._id === selectedTicketId);

    const StatusBadge = ({ status }) => {
        const styles = {
            pending: "bg-amber-100 text-amber-700",
            resolved: "bg-green-100 text-green-700",
            "in-progress": "bg-blue-100 text-blue-700"
        };
        return (
            <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase ${styles[status] || "bg-gray-100 text-gray-700"}`}>
                {status}
            </span>
        );
    };

    const handleBack = () => {
        setSearchParams({});
    };

    const handleNewTicket = () => {
        setSearchParams({ action: "new" });
    };

    const isMobileAction = selectedTicketId || isNewTicketAction;

    return (
        <div className="w-full h-full flex flex-col bg-gray-50/50">
            <div className="max-w-[1400px] mx-auto w-full h-full flex flex-col md:px-4 md:py-4 relative">
                <h1 className="hidden md:block text-2xl font-bold text-gray-900 mb-6 px-2 md:px-0">Support & Help Center</h1>

                <div className={`absolute md:relative inset-0 md:inset-auto ${isMobileAction ? 'z-[999]' : 'z-40'} md:flex-1 flex bg-white md:border md:border-gray-200 md:rounded-xl overflow-hidden md:shadow-sm`}>

                    {/* LEFT SIDEBAR - TICKET LIST */}
                    <div className={`w-full md:w-80 flex flex-col border-r border-gray-200 bg-gray-50/30 ${(selectedTicketId || isNewTicketAction) ? 'hidden md:flex' : 'flex'}`}>
                        <div className="p-4 bg-white border-b border-gray-200 space-y-3">
                            <div className="flex items-center justify-between">
                                <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wider">Tickets</h2>
                                <button
                                    onClick={handleNewTicket}
                                    className="p-1.5 bg-primary text-white rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-1 text-xs font-bold px-3"
                                    title="New Ticket"
                                >
                                    <Plus size={16} />
                                    <span>Raise Ticket</span>
                                </button>
                            </div>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    type="text"
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 bg-gray-100 border-none rounded-lg text-sm outline-none focus:ring-1 focus:ring-primary/30"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar bg-white">
                            {loadingTickets ? (
                                <div className="flex justify-center p-10"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" /></div>
                            ) : filteredTickets.length === 0 ? (
                                <div className="p-10 text-center text-gray-400 text-xs font-medium">No conversation history found</div>
                            ) : (
                                <div className="divide-y divide-gray-50">
                                    {filteredTickets.map((ticket) => (
                                        <button
                                            key={ticket._id}
                                            onClick={() => setSearchParams({ id: ticket._id })}
                                            className={`w-full p-4 flex flex-col gap-1 text-left transition-all ${selectedTicketId === ticket._id ? "bg-primary/5 border-l-4 border-l-primary" : "hover:bg-gray-50"}`}
                                        >
                                            <div className="flex justify-between items-center">
                                                <span className="text-[9px] font-bold text-gray-400 font-mono">#{ticket._id.slice(-6).toUpperCase()}</span>
                                                <StatusBadge status={ticket.status} />
                                            </div>

                                            <p className={`text-sm font-bold truncate mt-1 ${selectedTicketId === ticket._id ? "text-primary" : "text-gray-900"}`}>
                                                {ticket.issue_description}
                                            </p>

                                            <div className="flex justify-between items-end mt-1">
                                                <p className="text-[11px] text-gray-400 truncate flex-1 font-medium mr-2">
                                                    {ticket.messages && ticket.messages.length > 0
                                                        ? ticket.messages[ticket.messages.length - 1].message
                                                        : "No messages yet"}
                                                </p>
                                                <span className="text-[9px] text-gray-300 font-bold whitespace-nowrap">
                                                    {new Date(ticket.createdAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT AREA - CONTENT */}
                    <div className={`flex-1 flex flex-col bg-white overflow-hidden ${(selectedTicketId || isNewTicketAction) ? 'flex' : 'hidden md:flex'}`}>
                        {selectedTicketId && selectedTicket ? (
                            /* CHAT VIEW */
                            <div className="flex-1 flex flex-col overflow-hidden">
                                {/* Chat Header */}
                                <div className="px-4 md:px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white shadow-sm">
                                    <div className="flex items-center gap-3">
                                        <button onClick={handleBack} className="md:hidden p-1 -ml-1 text-gray-500 hover:bg-gray-100 rounded-full">
                                            <ChevronLeft size={24} />
                                        </button>
                                        <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                                            <MessageCircle size={20} />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-gray-900 text-sm">Case #{selectedTicket._id.slice(-6).toUpperCase()}</h3>
                                            <div className="flex items-center gap-2">
                                                <StatusBadge status={selectedTicket.status} />
                                                <span className="hidden sm:inline text-[10px] text-gray-400 font-medium">Engineering Response Hub</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <div className="hidden lg:flex items-center gap-6 text-sm text-gray-600 font-black">
                                            <a href="mailto:support@trrip.live" className="flex items-center gap-2 hover:text-primary transition-colors">
                                                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-primary">
                                                    <Mail size={14} />
                                                </div>
                                                support@trrip.live
                                            </a>
                                            <a href="tel:8530295666" className="flex items-center gap-2 hover:text-primary transition-colors">
                                                <div className="w-8 h-8 rounded-lg bg-gray-50 flex items-center justify-center text-primary">
                                                    <Phone size={14} />
                                                </div>
                                                +91 85302 95666
                                            </a>
                                        </div>
                                        <button onClick={handleBack} className="hidden md:block p-2 text-gray-400 hover:text-gray-600"><X size={20} /></button>
                                    </div>
                                </div>

                                {/* Messages Area */}
                                <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 bg-gray-50/50 custom-scrollbar">
                                    <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm max-w-2xl mx-auto">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase mb-2 block tracking-wider">Reported Incident</span>
                                        <p className="text-sm text-gray-700 leading-relaxed font-semibold">{selectedTicket.issue_description}</p>
                                        {selectedTicket.screenshot_url && (
                                            <a href={getAssetUrl(selectedTicket.screenshot_url)} target="_blank" rel="noreferrer" className="mt-4 block rounded-lg overflow-hidden border border-gray-50 hover:opacity-90 transition-all">
                                                <img src={getAssetUrl(selectedTicket.screenshot_url)} className="w-full max-h-60 object-contain bg-gray-50" alt="Attachment" />
                                            </a>
                                        )}
                                    </div>

                                    {selectedTicket.messages?.map((msg, idx) => (
                                        <div key={idx} className={`flex ${msg.sender_role === 'agent' ? 'justify-end' : 'justify-start'}`}>
                                            <div className={`max-w-[85%] md:max-w-[80%] flex flex-col ${msg.sender_role === 'agent' ? 'items-end' : 'items-start'}`}>
                                                <div className={`px-4 py-3 rounded-2xl text-sm shadow-sm font-medium ${msg.sender_role === 'agent'
                                                        ? 'bg-primary text-white rounded-tr-none'
                                                        : 'bg-white border border-gray-200 text-gray-800 rounded-tl-none'
                                                    }`}>
                                                    {msg.attachment_url && (
                                                        <a href={getAssetUrl(msg.attachment_url)} target="_blank" rel="noreferrer" className="block mb-2 rounded-lg overflow-hidden">
                                                            <img src={getAssetUrl(msg.attachment_url)} className="max-h-48 w-full object-cover" alt="Chat attachment" />
                                                        </a>
                                                    )}
                                                    {msg.message}
                                                </div>
                                                <span className="text-[9px] text-gray-400 mt-1 uppercase font-black tracking-tighter">
                                                    {msg.sender_role === 'agent' ? 'You' : 'Admin'} • {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={chatEndRef} />
                                </div>

                                {/* Chat Input */}
                                <div className="p-3 md:p-4 border-t border-gray-200 bg-white">
                                    {chatAttachmentPreview && (
                                        <div className="mb-3 relative inline-block">
                                            <img src={chatAttachmentPreview} className="h-16 w-16 object-cover rounded-lg border border-gray-200" alt="Preview" />
                                            <button onClick={() => { setChatAttachment(null); setChatAttachmentPreview(null); }} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors"><X size={10} /></button>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 md:gap-3">
                                        <button 
                                            disabled={isSendingMessage || selectedTicket.status === 'resolved'}
                                            onClick={() => chatFileInputRef.current.click()} 
                                            className={`p-2 transition-colors ${isSendingMessage || selectedTicket.status === 'resolved' ? "text-gray-200 cursor-not-allowed" : "text-gray-400 hover:text-primary"}`}
                                        >
                                            <Paperclip size={20} />
                                        </button>
                                        <input type="file" ref={chatFileInputRef} className="hidden" accept="image/*" onChange={handleChatAttachmentChange} disabled={isSendingMessage || selectedTicket.status === 'resolved'} />
                                        <input
                                            type="text"
                                            placeholder={selectedTicket.status === 'resolved' ? "Ticket resolved. No further messages." : "Type your message..."}
                                            value={ticketReplies[selectedTicket._id] || ""}
                                            onChange={(e) => setTicketReplies(prev => ({ ...prev, [selectedTicket._id]: e.target.value }))}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSendMessage(selectedTicket._id)}
                                            disabled={isSendingMessage || selectedTicket.status === 'resolved'}
                                            className={`flex-1 px-4 py-2.5 bg-gray-100 border-none rounded-xl text-sm outline-none focus:ring-1 focus:ring-primary/30 ${isSendingMessage || selectedTicket.status === 'resolved' ? "opacity-50 cursor-not-allowed" : ""}`}
                                        />
                                        <button
                                            disabled={isSendingMessage || selectedTicket.status === 'resolved' || (!ticketReplies[selectedTicket._id]?.trim() && !chatAttachment)}
                                            onClick={() => handleSendMessage(selectedTicket._id)}
                                            className={`p-2.5 rounded-xl transition-all ${isSendingMessage || selectedTicket.status === 'resolved' || (!ticketReplies[selectedTicket._id]?.trim() && !chatAttachment) ? "text-gray-300" : "bg-primary text-white shadow-sm hover:bg-teal-700"}`}
                                        >
                                            {isSendingMessage ? <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" /> : <Send size={18} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            /* RAISE TICKET FORM */
                            <div className="flex-1 overflow-y-auto p-4 md:p-12 bg-white">
                                <div className="max-w-xl mx-auto space-y-6">
                                    <div className="flex items-center gap-4 mb-8">
                                        <button onClick={handleBack} className="md:hidden p-1 -ml-1 text-gray-500 hover:bg-gray-100 rounded-full">
                                            <ChevronLeft size={24} />
                                        </button>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900">New Support Ticket</h3>
                                            <p className="text-xs text-gray-500 font-medium tracking-tight">Tell us what's your issue and we'll fix it.</p>
                                        </div>
                                    </div>

                                    {/* Mobile Contact Info */}
                                    <div className="grid grid-cols-1 gap-3 md:hidden">
                                        <a href="mailto:support@trrip.live" className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 active:scale-[0.98] transition-all">
                                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-primary shadow-sm">
                                                <Mail size={20} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Email Us</p>
                                                <p className="text-sm font-black text-gray-600">support@trrip.live</p>
                                            </div>
                                        </a>
                                        <a href="tel:8530295666" className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border border-gray-100 active:scale-[0.98] transition-all">
                                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-primary shadow-sm">
                                                <Phone size={20} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest leading-none mb-1">Call Support</p>
                                                <p className="text-sm font-black text-gray-600">+91 85302 95666</p>
                                            </div>
                                        </a>
                                    </div>

                                    {/* Desktop Contact Row */}
                                    <div className="hidden md:flex items-center gap-10 pb-8 mb-8 border-b border-gray-100">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-[#067978]/5 rounded-2xl flex items-center justify-center text-[#067978] shadow-sm">
                                                <Mail size={22} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.1em] mb-1">Email Us</p>
                                                <a href="mailto:support@trrip.live" className="text-base font-black text-gray-600 hover:text-primary transition-colors">support@trrip.live</a>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center text-[#067978] shadow-sm">
                                                <Phone size={22} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.1em] mb-1">Call Support</p>
                                                <a href="tel:8530295666" className="text-base font-black text-gray-600 hover:text-primary transition-colors">+91 85302 95666</a>
                                            </div>
                                        </div>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">My Identity</label>
                                                <input value={userData?.name || ""} disabled className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border-transparent text-gray-600 text-sm font-black cursor-not-allowed" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Relay Email</label>
                                                <input value={userData?.email || ""} disabled className="w-full px-4 py-2.5 rounded-xl bg-gray-50 border-transparent text-gray-600 text-sm font-black cursor-not-allowed" />
                                            </div>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Observation</label>
                                            <textarea
                                                value={issue}
                                                onChange={(e) => setIssue(e.target.value)}
                                                rows="5"
                                                placeholder="Describe the issue in detail..."
                                                disabled={isSubmitting}
                                                className={`w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-1 focus:ring-primary focus:border-primary transition-all resize-none text-sm outline-none font-medium ${isSubmitting ? "bg-gray-50 opacity-50 cursor-not-allowed" : ""}`}
                                            ></textarea>
                                        </div>

                                        <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Visual Evidence</label>
                                            <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl transition-all overflow-hidden group bg-gray-50/50 ${isSubmitting ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-50"}`}>
                                                {screenshotPreview ? (
                                                    <img src={screenshotPreview} className="h-full w-full object-contain p-2" alt="Preview" />
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center py-5">
                                                        <ImageIcon className="text-gray-300 group-hover:text-primary mb-2" size={24} />
                                                        <p className="text-xs text-gray-400 font-bold uppercase tracking-tighter">Capture Image</p>
                                                    </div>
                                                )}
                                                <input type="file" accept="image/*" className="hidden" onChange={handleScreenshotChange} disabled={isSubmitting} />
                                            </label>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className={`w-full py-4 rounded-xl font-black flex items-center justify-center gap-2 transition-all ${isSubmitting ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-primary text-white hover:bg-teal-700 shadow-xl shadow-primary/20 active:scale-[0.98]"}`}
                                        >
                                            {isSubmitting ? "TRANSMITTING..." : "OPEN CASE"}
                                            {!isSubmitting && <Send size={18} />}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #E5E7EB;
                    border-radius: 10px;
                }
            `}} />
        </div>
    );
};

export default SupportPage;
